import React, { ReactNode } from "react";
import { Permission } from "../../../__generated__/api";
import Toggle from "../../theme/toggle/Toggle";
import Styles from "./Permissions.module.scss";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";
import { Filters } from "../../filters/Filters";

export const PERMISSION_MAP: Record<string, {name: string; description: string}> = {
    [Permission.Administrator]: {
        name: "Administrator",
        description: "Full administrator with access to everything available on the account.",
    },
    [Permission.ArticleModify]: {
        name: "Modify Articles",
        description: "Modify all articles",
    },
    [Permission.ArticleRead]: {
        name: "Read all Articles",
        description: "Read all articles published or not",
    },
    [Permission.ArticleReadPublic]: {
        name: "Read public articles",
        description: "Read all public, published articles",
    },
    [Permission.ChatsModify]: {
        name: "Manage chats",
        description: "Manage conversations with customers",
    },
    [Permission.ChatsModifyAll]: {
        name: "Manage all chats",
        description: "Manage all conversations with customers",
    },
    [Permission.ChatsRead]: {
        name: "Read chats",
        description: "Read Conversations with customers",
    },
    [Permission.ChatsReadAll]: {
        name: "Read all chats",
        description: "Read all conversations with customers",
    },
    [Permission.GroupsRead]: {
        name: "Read Groups",
        description: "Read groups",
    },
    [Permission.GroupsWrite]: {
        name: "Write Groups",
        description: "Create/Write groups",
    },
    [Permission.ReadIntegration]: {
        name: "Read Integrations",
        description: "View current Integrations",
    },
    [Permission.RegisterIntegration]: {
        name: "Install integrations",
        description: "Install new integrations",
    },
    [Permission.Reports]: {
        name: "View Reports",
        description: "View reports APIs via token",
    },
    [Permission.TicketsComment]: {
        name: "Comment on Tickets",
        description: "Comment and message tickets",
    },
    [Permission.TicketsModify]: {
        name: "Modify all Tickets",
        description: "Manager and modify all tickets",
    },
    [Permission.TicketsRead]: {
        name: "Read Tickets",
        description: "Read tickets.",
    },
    [Permission.TicketsReadAll]: {
        name: "Read all Tickets",
        description: "Read all tickets",
    },
    [Permission.TicketsReadSelf]: {
        name: "Read self Tickets",
        description: "Read tickets created by the current user",
    },
    [Permission.TicketCreate]: {
        name: "Create Tickets",
        description: "Create Tickets",
    },
    [Permission.TicketCreateGuest]: {
        name: "Create Tickets (Guest)",
        description: "Create tickets as a guest user.",
    },
    [Permission.TicketStateModify]: {
        name: "Modify Columns",
        description: "Manage columns and states.",
    },
    [Permission.UsersCreate]: {
        name: "Create Users",
        description: "Create new users",
    },
    [Permission.UsersRead]: {
        name: "Read users",
        description: "View all users",
    },
    [Permission.UsersWrite]: {
        name: "Modify Users",
        description: "Manage and modify users",
    },

};

export const PERMISSION_OPTIONS = Object.entries(PERMISSION_MAP).map(([key, value]) => ({
    value: key,
    label: value.name,
    data: key as Permission,
}));

const PERMISSION_OPTIONS_MAP = PERMISSION_OPTIONS.toIdMap("value");

export interface PermissionsPickerProps<TIsMulti = false, ValueIdType = TIsMulti extends true ? Permission[] : Permission> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    display?: "list" | "filters" | "dropdown";
    className?: string;
    toggleClassName?: string;
    toggleText?: string
    searchMinimum?: number;
    value: (TIsMulti extends true ? Permission[] : Permission) | undefined;
    onChange: (sourceId: ValueIdType, texts: ValueIdType) => void;
    icon?: ReactNode;
    shouldDisplayAll?: boolean;
}

const PermissionsList = React.forwardRef(<TIsMulti extends boolean = false>({
    isMulti, onChange, placeholder, minRequired, value, className, display, toggleText, toggleClassName,
    searchMinimum, icon, shouldDisplayAll,
}: PermissionsPickerProps<TIsMulti>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactNode => {
    if (isMulti) {
        //
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Permission>[] : Option<Permission>;
    type ValueType = TIsMulti extends true ? Option<Permission>[] : Option<Permission>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Permission>).value) as Parameters<typeof onChange>[0];
        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label as string)
            : (selected as Option<Permission>).label) as Parameters<typeof onChange>[0];
        onChange(values, texts);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    const selected = Array.isArray(value)
        ? value.map(row => PERMISSION_OPTIONS_MAP[row]).filterNull()
        : PERMISSION_OPTIONS_MAP[value as Permission];
    if (display === "filters") {
        return (
            <Filters
                icon={icon}
                toggleText={toggleText}
                isMulti={isMulti}
                categories={PERMISSION_OPTIONS}
                searchMinimum={searchMinimum}
                className={className}
                toggleClassName={toggleClassName}
                shouldDisplayAll={shouldDisplayAll}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onChange as ((value: string[]) => void))(Array.from(current))
                    : (onChange as ((value: string) => void))(current as string))}
            />
        );
    } if (display === "dropdown") {
        return (
            <Select
                ref={ref}
                isSortable
                className={className}
                minRequired={minRequired}
                placeholder={placeholder}
                isMulti={isMulti}
                onChange={handleSelected}
                options={PERMISSION_OPTIONS}
                value={selected as ValueType}
            />
        );
    }

    const valueSet = new Set(Array.isArray(value) ? value : [value]);
    return (
        <div className={Styles["token-list"]}>
            {Object.entries(PERMISSION_MAP).map(([key, permission]) => (
                <div className={Styles["token-item"]} key={key}>

                    <div className={Styles["token-name"]}>
                        {permission.name}
                    </div>
                    <div className={Styles["token-description"]}>
                        {permission.description}
                    </div>

                    <Toggle
                        checked={valueSet.has(key as Permission)}
                        onChange={evt => {
                            let list = (Array.isArray(value) ? value : [value]) as Permission[];
                            if (evt.target.checked) {
                                list.push(key as Permission);
                            } else {
                                list = list.filter(row => row !== key);
                            }

                            if (isMulti) {
                                (onChange as ((value: Permission[]) => void))(list);
                            } else {
                                (onChange as ((value: Permission) => void))(list[0]);
                            }
                        }}
                    />

                </div>
            ))}
        </div>
    );
});

PermissionsList.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    display: "list",
    className: "",
    toggleClassName: "",
    toggleText: undefined,
    searchMinimum: undefined,
    icon: undefined,
    shouldDisplayAll: false,
};

export default PermissionsList as <
    TMulti extends boolean = false,
> (
    props: PermissionsPickerProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof PermissionsList>;
